import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CircularProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { AlertMessage, MainHeaderWithButton } from '@eposnow/ui-core';
import { Category, ProductCreateEdit, ProductSuggestion } from '../../../types';
import { UIContext } from '../../../context/UIContext';
import { deepClone } from '../../../helpers/helpers';
import BuildColumns from './helpers/Columns';
import useBusinessInformation from '../../../helperHooks/useBusinessInformation';
import { UserContext } from '../../../context/UserContext';

const SidekickDesktopProductTable = ({
  data,
  isAddingProducts,
  addProducts,
  loading,
  categories,
  refetchProducts,
}: {
  data: ProductSuggestion[];
  isAddingProducts: boolean;
  addProducts: (newProducts: ProductCreateEdit[]) => void;
  loading: boolean;
  categories: Category[];
  refetchProducts: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const {
    currencySymbol,
    currencyISOCode,
    isLoading: isLoadingCurrency,
  } = useBusinessInformation();
  const { isTablet, isMobile, isMediumDevice } = useContext(UIContext);
  const { locale } = useContext(UserContext);
  const [errorMessage, setErrorMessage] = useState('');
  const [rows, setRows] = useState(data);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const parseAndAddProducts = (id?: number) => {
    if (data?.length === 0) return;
    if (id != null) {
      const selectedProduct: ProductSuggestion = deepClone(
        rows.find((row) => row.placeholderId === id?.toString())
      );
      const newProduct: ProductCreateEdit = {
        name: selectedProduct.name.replace(/[',<>#]/g, ''),
        salePrice: selectedProduct.price,
        categoryId: selectedProduct.categoryId === -1 ? null : selectedProduct.categoryId,
        description: selectedProduct.description.replace(/["'\\#]/g, ''),
        costPrice: 0,
      };
      addProducts([newProduct]);
    } else {
      addProducts(
        rows.map((row) => ({
          name: row.name.replace(/[',<>#]/g, ''),
          salePrice: row.price,
          description: row.description.replace(/["'\\#]/g, ''),
          categoryId: row.categoryId === -1 ? null : row.categoryId,
          costPrice: 0,
        }))
      );
    }
  };

  const autosizeOptions = {
    includeOutliers: true,
    includeHeaders: true,
    expand: true,
  };

  const handleAddClick = (id: number) => () => {
    parseAndAddProducts(id);
  };

  const handleDuplicateClick = (id: number) => () => {
    const newRow = deepClone(rows.find((row) => row.placeholderId === id?.toString()));
    const newRows = deepClone(rows);
    newRows.splice(id, 0, newRow);
    setRows(newRows.map((row, index) => ({ ...row, placeholderId: index?.toString() })));
  };

  const handleEditClick = (id: number) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: number) => () => {
    gridApiRef?.current?.stopRowEditMode({ id });
  };

  const handleDeleteClick = (id: number) => () => {
    const newRows = rows.filter((row) => row.placeholderId !== id?.toString());
    setRows(newRows.map((row, index) => ({ ...row, placeholderId: index?.toString() })));
  };

  const handleCancelClick = (id: number) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: 10 });
    setRows(data);
  }, [data]);

  const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
    setRows(
      rows.map((row) =>
        row.placeholderId === newRow.placeholderId ? (newRow as ProductSuggestion) : row
      )
    );
    return newRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleProcessRowUpdateError = (err: Error) => {
    setErrorMessage(err?.message || t('errors.genericError'));
  };

  return (
    <>
      <Box
        sx={{
          marginBottom: { xs: 2, sm: 3 },
          marginLeft: 0,
          paddingX: { xs: 2, sm: 0 },
          ...(isMobile && {
            marginX: '-1rem',
          }),
        }}
      >
        <MainHeaderWithButton title={t(`steps.products.title`)} isMobile={isMobile} theme={theme}>
          {data?.length > 0 && (
            <Button
              color="primary"
              data-qa-id="button-add-to-catalogue"
              variant="contained"
              disableElevation
              onClick={() => parseAndAddProducts()}
            >
              {isAddingProducts && (
                <>
                  <CircularProgress
                    aria-label="Adding products"
                    aria-live="polite"
                    aria-busy={isAddingProducts}
                    color="inherit"
                    size="20px"
                    sx={{ marginRight: 1 }}
                  />
                  {t('screens.products.sidekick.adding')}
                </>
              )}
              {!isAddingProducts &&
                (isMobile ? t('actions.add') : t('screens.products.sidekick.addAllProducts'))}
            </Button>
          )}
        </MainHeaderWithButton>

        <Typography variant="body1" sx={{ paddingTop: '0.5rem' }}>
          {t(`steps.products.description`)}
        </Typography>
      </Box>

      {errorMessage && (
        <AlertMessage
          type="error"
          styles={{ marginBottom: 2 }}
          action={() => setErrorMessage('')}
          locale={locale}
          theme={theme}
        >
          {errorMessage}
        </AlertMessage>
      )}

      {data?.length > 0 ? (
        <Box
          sx={{
            height: {
              xs: `calc(100vh - 250px - 64px)`,
              sm: `calc(100vh - 250px - 64px)`,
              md: `calc(100vh - 365.5px - 64px)`,
              lg: `calc(100vh - 365.5px - 64px)`,
              xl: `calc(100vh - 365.5px - 64px)`,
            },
            ...(!isMobile && !isTablet && { minHeight: '300px' }),
            width: '100%',
            marginBottom: '4rem',
          }}
        >
          <DataGridPro
            rows={rows || []}
            editMode="row"
            apiRef={gridApiRef}
            getRowId={(row: ProductSuggestion) => row.placeholderId}
            columns={BuildColumns(
              rowModesModel,
              handleSaveClick,
              handleCancelClick,
              handleEditClick,
              handleDeleteClick,
              handleAddClick,
              handleDuplicateClick,
              categories,
              { currencyISOCode, currencySymbol, isLoading: isLoadingCurrency }
            )}
            pagination
            autosizeOnMount
            autosizeOptions={autosizeOptions}
            data-qa-id="table-products"
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
            rowHeight={48}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            columnHeaderHeight={72}
            disableRowSelectionOnClick
            columnBuffer={4}
            disableVirtualization={process.env.NODE_ENV === 'test'}
            loading={loading}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            slotProps={{
              pagination: {
                labelRowsPerPage: t('components.table.rowsPerPage'),
                labelDisplayedRows: (paginationInfo) =>
                  t('components.table.displayedRows', {
                    from: paginationInfo.from,
                    to: paginationInfo.to,
                    count: paginationInfo.count,
                  }),
              },
            }}
            sx={{
              backgroundColor: 'background.paper',
              '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                alignItems: 'center',
              },
              '.MuiDataGrid-iconButtonContainer': {
                visibility: 'visible',
              },
              '.MuiDataGrid-row.MuiDataGrid-row--lastVisible': {
                '.MuiDataGrid-cell': {
                  borderBottom: 0,
                },
              },
              '& ::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto',
              },
              '& ::-webkit-scrollbar-track': {
                backgroundColor: theme?.palette?.grey?.['8p'],
                borderRadius: '10px',
              },
              '& ::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: theme?.palette?.grey?.['50p'],
              },
              '.MuiDataGrid-cell--editing': {
                '&:first-of-type': {
                  input: {
                    pl: 0,
                  },
                },
                input: {
                  pl: 1.5,
                },
              },
              '.MuiDataGrid-columnHeader': {
                padding: '0 12px',
                ...(isTablet && {
                  padding: '0 8px',
                }),
                '&:first-of-type': {
                  pl: 2,
                  ...(!isMediumDevice && {
                    pl: 1,
                  }),
                },
                '&:last-child': {
                  pr: 2,
                  ...(!isMediumDevice && {
                    pr: 1,
                  }),
                },
              },
              '.MuiDataGrid-cell': {
                padding: '0 12px',
                ...(isTablet && {
                  padding: '0 8px',
                }),
                '&:first-of-type': {
                  pl: 1,
                  ...(!isMediumDevice && {
                    pl: 1,
                  }),
                },
                '&:has(.Mui-error)': {
                  border: '2px solid #D32F2F',
                },
                '&:has(.Mui-focused):has(.Mui-error)': {
                  outline: 'none',
                },
              },
              ...(data?.length === 0 && {
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'hidden',
                },
              }),
            }}
          />
        </Box>
      ) : (
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '1.5rem',
            marginBottom: '3rem',
            gap: '1rem',
          }}
        >
          <Typography>{t('screens.products.sidekick.noProductsGenerated')}</Typography>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              sx={{
                height: '48px',
              }}
              onClick={refetchProducts}
            >
              {t('actions.retry')}
            </Button>
          </Box>
        </Card>
      )}
    </>
  );
};
export default SidekickDesktopProductTable;
